import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { ArrowLeft, Users, GraduationCap, Briefcase, Church, BarChart3, PieChart as PieChartIcon, TrendingUp } from 'lucide-react'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from 'recharts'
import { usePendudukStats } from '../hooks/use-penduduk-stats'
import { usePendidikanStats } from '../hooks/use-pendidikan-stats'
import { usePekerjaanStats } from '../hooks/use-pekerjaan-stats'
import { useAgamaStats } from '../hooks/use-agama-stats'
import { Button } from '../components/ui/button'
import { Spinner } from '../components/ui/spinner'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card'
import { ChartContainer, ChartTooltipContent, ChartConfig } from '../components/ui/chart'
import { PendudukStatistics, StatistikPendidikan, StatistikPekerjaan, StatistikAgama, RangeUmur, PerDusun, StatistikItemKategori, ApiResponse } from '../../../shared-lib/types'

export default function StatisticsDetailPage() {
    const { category } = useParams()
    const navigate = useNavigate()
    
    const { data: penduduk, isLoading: loadingPenduduk } = usePendudukStats()
    const { data: pendidikan, isLoading: loadingPendidikan } = usePendidikanStats()
    const { data: pekerjaan, isLoading: loadingPekerjaan } = usePekerjaanStats()
    const { data: agama, isLoading: loadingAgama } = useAgamaStats()

    if (!category) return null

    let title = ''
    let icon = null
    let data: PendudukStatistics | StatistikPendidikan | StatistikPekerjaan | StatistikAgama | null | undefined = null
    let isLoading = false
    let color = ''

    switch (category) {
        case 'penduduk':
            title = 'Detail Statistik Penduduk'
            icon = <Users className="h-6 w-6" />
            data = penduduk?.data
            isLoading = loadingPenduduk
            color = 'blue'
            break
        case 'pendidikan':
            title = 'Detail Statistik Pendidikan'
            icon = <GraduationCap className="h-6 w-6" />
            data = pendidikan?.data
            isLoading = loadingPendidikan
            color = 'green'
            break
        case 'pekerjaan':
            title = 'Detail Statistik Pekerjaan'
            icon = <Briefcase className="h-6 w-6" />
            data = pekerjaan?.data
            isLoading = loadingPekerjaan
            color = 'orange'
            break
        case 'agama':
            title = 'Detail Statistik Agama'
            icon = <Church className="h-6 w-6" />
            data = agama?.data
            isLoading = loadingAgama
            color = 'purple'
            break
        default:
            return <div>Kategori tidak ditemukan</div>
    }

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
                <div className="text-center">
                    <Spinner className="h-12 w-12 mx-auto mb-4" />
                    <h3 className="text-lg font-medium">Memuat Data Detail</h3>
                    <p className="text-muted-foreground">Mengambil informasi lengkap...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
            {/* Header */}
            <div className={`bg-gradient-to-r ${getColorGradient(color)} relative overflow-hidden`}>
                <div className="relative container mx-auto px-6 py-12">
                    <Button
                        variant="outline"
                        onClick={() => navigate('/statistics')}
                        className="mb-6 bg-white/10 border-white/20 text-white hover:bg-white/20"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Kembali ke Dashboard
                    </Button>
                    
                    <div className="flex items-center space-x-4">
                        <div className="p-4 bg-white/20 rounded-full backdrop-blur-sm">
                            {icon}
                        </div>
                        <div>
                            <h1 className="text-4xl font-bold text-white mb-2">{title}</h1>
                            <p className="text-white/80 text-lg">
                                Analisis mendalam dan visualisasi data {category}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="container mx-auto px-6 py-8">
                {category === 'penduduk' && data && 'jumlahLakiLaki' in data && (
                    <PendudukDetailView data={data as PendudukStatistics} />
                )}
                {(category === 'pendidikan' || category === 'pekerjaan' || category === 'agama') && data && (
                    <CategoryDetailView data={data as StatistikPendidikan | StatistikPekerjaan | StatistikAgama} category={category} color={color} />
                )}
            </div>
        </div>
    )
}

// Helper function to get color gradients
function getColorGradient(color: string) {
    const gradients = {
        blue: 'from-blue-600 via-blue-700 to-blue-800',
        green: 'from-green-600 via-green-700 to-green-800',
        orange: 'from-orange-600 via-orange-700 to-orange-800',
        purple: 'from-purple-600 via-purple-700 to-purple-800'
    }
    return gradients[color as keyof typeof gradients] || gradients.blue
}

// Penduduk Detail View
function PendudukDetailView({ data }: { data: PendudukStatistics }) {
    return (
        <div className="space-y-8">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <DetailCard 
                    title="Total Penduduk"
                    value={data.totalPenduduk}
                    color="blue"
                    subtitle="Jumlah keseluruhan"
                    icon={<Users className="h-6 w-6" />}
                />
                <DetailCard 
                    title="Kepala Keluarga"
                    value={data.totalKepalaKeluarga}
                    color="green"
                    subtitle={`${data.persentaseKepalaKeluarga?.toFixed(1)}% dari total penduduk`}
                    icon={<Users className="h-6 w-6" />}
                />
                <DetailCard 
                    title="Laki-laki"
                    value={data.jumlahLakiLaki}
                    color="blue"
                    subtitle={`${data.persentaseLakiLaki?.toFixed(1)}% dari total`}
                    icon={<Users className="h-6 w-6" />}
                />
                <DetailCard 
                    title="Perempuan"
                    value={data.jumlahPerempuan}
                    color="purple"
                    subtitle={`${data.persentasePerempuan?.toFixed(1)}% dari total`}
                    icon={<Users className="h-6 w-6" />}
                />
            </div>

            {/* Enhanced Gender Analysis */}
            <EnhancedGenderChart 
                lakiLaki={data.jumlahLakiLaki} 
                perempuan={data.jumlahPerempuan} 
            />

            {/* Enhanced Age Analysis */}
            <EnhancedAgeChart ageData={data.rangeUmur || []} />

            {/* Enhanced Regional Analysis */}
            {data.perDusun && data.perDusun.length > 0 && (
                <EnhancedRegionalChart dusunData={data.perDusun} />
            )}
        </div>
    )
}

// Category Detail View (for pendidikan, pekerjaan, agama)
function CategoryDetailView({ data, category, color }: { data: StatistikPendidikan | StatistikPekerjaan | StatistikAgama, category: string, color: string }) {
    const getCategoryData = (): StatistikItemKategori[] => {
        if ('pendidikan' in data) return data.pendidikan
        if ('pekerjaan' in data) return data.pekerjaan
        if ('agama' in data) return data.agama
        return []
    }
    
    const allCategoryData = getCategoryData()
    // Filter to only show categories with data (jumlah > 0)
    const categoryData = allCategoryData.filter(item => item.jumlah > 0)
    
    return (
        <div className="space-y-8">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <DetailCard 
                    title="Total Penduduk"
                    value={data.totalPenduduk}
                    color={color}
                    icon={<Users className="h-6 w-6" />}
                />
                <DetailCard 
                    title={`Kategori ${category.charAt(0).toUpperCase() + category.slice(1)} dengan Data`}
                    value={categoryData.length}
                    color={color}
                    icon={<BarChart3 className="h-6 w-6" />}
                />
                <DetailCard 
                    title="Kategori Terbesar"
                    value={categoryData[0]?.nama || 'N/A'}
                    color={color}
                    subtitle={`${categoryData[0]?.persentase?.toFixed(1) || 0}%`}
                    icon={<TrendingUp className="h-6 w-6" />}
                    isText={true}
                />
            </div>

            {/* Pie Chart for categories with data only */}
            {categoryData.length > 0 && (
                <div className="bg-white rounded-xl border p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-6 flex items-center">
                        <PieChartIcon className={`h-6 w-6 ${
                            category === 'pendidikan' ? 'text-green-600' : 
                            category === 'pekerjaan' ? 'text-orange-600' : 
                            'text-purple-600'
                        } mr-2`} />
                        Distribusi {category.charAt(0).toUpperCase() + category.slice(1)} Komprehensif
                    </h3>
                    <DetailPieChart data={categoryData} color={color} />
                </div>
            )}

            {/* Detailed Analysis - Only show categories with data */}
            {categoryData.length > 0 && (
                <div className="bg-white rounded-xl border p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-6">
                        Analisis Detail {category.charAt(0).toUpperCase() + category.slice(1)}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {categoryData.map((item, index) => (
                            <div key={index} className="p-4 bg-gray-50 rounded-lg">
                                <div className="flex justify-between items-start mb-2">
                                    <h5 className="font-medium text-gray-900">{item.nama}</h5>
                                    <span className={`px-2 py-1 bg-${color}-100 text-${color}-800 text-xs rounded-full`}>
                                        {item.persentase?.toFixed(1)}%
                                    </span>
                                </div>
                                <p className="text-sm text-gray-600 mb-3">
                                    Total: {item.jumlah} jiwa
                                </p>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-blue-600 font-medium">Laki-laki</span>
                                        <p>{item.lakiLaki} jiwa ({item.persentaseLakiLaki?.toFixed(1)}%)</p>
                                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                            <div 
                                                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${item.persentaseLakiLaki}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-pink-600 font-medium">Perempuan</span>
                                        <p>{item.perempuan} jiwa ({item.persentasePerempuan?.toFixed(1)}%)</p>
                                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                            <div 
                                                className="bg-pink-500 h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${item.persentasePerempuan}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Show message if no data available */}
            {categoryData.length === 0 && (
                <div className="bg-white rounded-xl border p-8 shadow-sm text-center">
                    <div className="text-gray-500 mb-4">
                        <BarChart3 className="h-12 w-12 mx-auto opacity-50" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Tidak Ada Data {category.charAt(0).toUpperCase() + category.slice(1)}
                    </h3>
                    <p className="text-gray-600">
                        Belum ada data {category} yang tersedia untuk ditampilkan.
                    </p>
                </div>
            )}
        </div>
    )
}

interface DetailCardProps {
    title: string
    value: string | number
    color: string
    subtitle?: string
    icon?: React.ReactNode
    isText?: boolean
}

// Detail Card Component
function DetailCard({ title, value, color, subtitle, icon, isText = false }: DetailCardProps) {
    const colorMap = {
        blue: 'from-blue-500 to-blue-600',
        green: 'from-green-500 to-green-600',
        orange: 'from-orange-500 to-orange-600',
        purple: 'from-purple-500 to-purple-600',
        pink: 'from-pink-500 to-pink-600'
    }

    return (
        <div className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
                <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
                    <p className={`text-2xl font-bold text-gray-900 ${isText ? 'text-lg' : ''}`}>
                        {isText ? value : (typeof value === 'number' ? value.toLocaleString() : value)}
                    </p>
                    {subtitle && (
                        <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
                    )}
                </div>
                <div className={`p-3 rounded-full bg-gradient-to-br ${colorMap[color as keyof typeof colorMap]} text-white`}>
                    {icon}
                </div>
            </div>
        </div>
    )
}

// Detail Age Chart
function DetailAgeChart({ ageData }: { ageData: RangeUmur[] }) {
    const maxValue = Math.max(...ageData.map(item => item.jumlah))
    
    return (
        <div className="space-y-4">
            {ageData.map((item, index) => (
                <div key={item.range} className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-700">{item.range} tahun</span>
                        <div className="text-right">
                            <span className="text-lg font-bold">{item.jumlah}</span>
                            <span className="text-sm text-gray-500 ml-2">({item.persentase.toFixed(1)}%)</span>
                        </div>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-4">
                        <div 
                            className="bg-gradient-to-r from-blue-400 to-blue-600 h-4 rounded-full transition-all duration-1000"
                            style={{ width: `${(item.jumlah / maxValue) * 100}%` }}
                        ></div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                            <span className="text-blue-700">Laki-laki</span>
                            <span className="font-medium">{item.lakiLaki}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-pink-50 rounded-lg">
                            <span className="text-pink-700">Perempuan</span>
                            <span className="font-medium">{item.perempuan}</span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}

// Detail Dusun Chart
function DetailDusunChart({ dusunData }: { dusunData: PerDusun[] }) {
    return (
        <div className="space-y-4">
            {dusunData.map((dusun, index) => (
                <div key={dusun.dusunId || index} className="p-4 border rounded-lg hover:shadow-sm transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{dusun.namaDusun}</h4>
                        <span className="text-sm text-gray-500">{dusun.persentase.toFixed(1)}%</span>
                    </div>
                    
                    <div className="text-2xl font-bold text-gray-900 mb-2">{dusun.jumlah}</div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-blue-600">♂ {dusun.lakiLaki}</div>
                        <div className="text-pink-600">♀ {dusun.perempuan}</div>
                    </div>
                </div>
            ))}
        </div>
    )
}

// Detail Category Chart
function DetailCategoryChart({ data, color }: { data: StatistikItemKategori[], color: string }) {
    const colorMap = {
        green: 'from-green-400 to-green-600',
        orange: 'from-orange-400 to-orange-600',
        purple: 'from-purple-400 to-purple-600'
    }
    
    const maxValue = Math.max(...data.map(item => item.jumlah))
    
    return (
        <div className="space-y-6">
            {data.map((item, index) => (
                <div key={item.nama} className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900">{item.nama}</h4>
                        <div className="text-right">
                            <span className="text-lg font-bold">{item.jumlah}</span>
                            <span className="text-sm text-gray-500 ml-2">({item.persentase.toFixed(1)}%)</span>
                        </div>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-4">
                        <div 
                            className={`bg-gradient-to-r ${colorMap[color as keyof typeof colorMap]} h-4 rounded-full transition-all duration-1000`}
                            style={{ width: `${(item.jumlah / maxValue) * 100}%` }}
                        ></div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                            <div className="font-medium">{item.jumlah}</div>
                            <div className="text-gray-500">Total</div>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                            <div className="font-medium text-blue-700">{item.lakiLaki}</div>
                            <div className="text-blue-600">Laki-laki</div>
                        </div>
                        <div className="text-center p-3 bg-pink-50 rounded-lg">
                            <div className="font-medium text-pink-700">{item.perempuan}</div>
                            <div className="text-pink-600">Perempuan</div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}

function EnhancedGenderChart({ lakiLaki, perempuan }: { lakiLaki: number; perempuan: number }) {
    const [hoveredSegment, setHoveredSegment] = useState<string | null>(null)
    
    const total = lakiLaki + perempuan
    const percentageLakiLaki = total > 0 ? (lakiLaki / total) * 100 : 0
    const percentagePerempuan = total > 0 ? (perempuan / total) * 100 : 0
    
    const radius = 90
    const center = 120
    const circumference = 2 * Math.PI * radius
    
    const lakiLakiArc = (percentageLakiLaki / 100) * circumference
    const perempuanArc = (percentagePerempuan / 100) * circumference
    
    return (
        <div className="bg-white p-8 rounded-xl shadow-lg border">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <Users className="h-6 w-6 text-blue-600 mr-2" />
                Analisis Gender Mendalam
            </h3>
            
            <div className="flex flex-col lg:flex-row items-center space-y-8 lg:space-y-0 lg:space-x-12">
                <div className="relative">
                    <svg width="240" height="240" className="transform -rotate-90">
                        {/* Background circle */}
                        <circle
                            cx={center}
                            cy={center}
                            r={radius}
                            stroke="#f3f4f6"
                            strokeWidth="24"
                            fill="transparent"
                        />
                        
                        {/* Laki-laki segment */}
                        <circle
                            cx={center}
                            cy={center}
                            r={radius}
                            stroke={hoveredSegment === 'laki-laki' ? "#1d4ed8" : "#3b82f6"}
                            strokeWidth="24"
                            fill="transparent"
                            strokeDasharray={`${lakiLakiArc} ${circumference}`}
                            strokeLinecap="round"
                            className="cursor-pointer transition-all duration-300 drop-shadow-md"
                            onMouseEnter={() => setHoveredSegment('laki-laki')}
                            onMouseLeave={() => setHoveredSegment(null)}
                        />
                        
                        {/* Perempuan segment */}
                        <circle
                            cx={center}
                            cy={center}
                            r={radius}
                            stroke={hoveredSegment === 'perempuan' ? "#db2777" : "#ec4899"}
                            strokeWidth="24"
                            fill="transparent"
                            strokeDasharray={`${perempuanArc} ${circumference}`}
                            strokeDashoffset={-lakiLakiArc}
                            strokeLinecap="round"
                            className="cursor-pointer transition-all duration-300 drop-shadow-md"
                            onMouseEnter={() => setHoveredSegment('perempuan')}
                            onMouseLeave={() => setHoveredSegment(null)}
                        />
                    </svg>
                    
                    {/* Center text */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                            <p className="text-3xl font-bold text-gray-900">{total.toLocaleString('id-ID')}</p>
                            <p className="text-sm text-gray-600">Total Penduduk</p>
                        </div>
                    </div>
                </div>
                
                <div className="flex-1 space-y-6">
                    <div 
                        className={`p-6 rounded-xl transition-all duration-300 cursor-pointer ${
                            hoveredSegment === 'laki-laki' ? 'bg-blue-50 scale-105 border-blue-200 border-2' : 'bg-blue-25 border border-blue-100'
                        }`}
                        onMouseEnter={() => setHoveredSegment('laki-laki')}
                        onMouseLeave={() => setHoveredSegment(null)}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="w-6 h-6 bg-blue-500 rounded-full"></div>
                                <div>
                                    <h4 className="text-lg font-bold text-blue-900">Laki-laki</h4>
                                    <p className="text-sm text-blue-700">Populasi pria</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-2xl font-bold text-blue-900">{lakiLaki.toLocaleString('id-ID')}</p>
                                <p className="text-sm text-blue-700">{percentageLakiLaki.toFixed(1)}% dari total</p>
                            </div>
                        </div>
                    </div>
                    
                    <div 
                        className={`p-6 rounded-xl transition-all duration-300 cursor-pointer ${
                            hoveredSegment === 'perempuan' ? 'bg-pink-50 scale-105 border-pink-200 border-2' : 'bg-pink-25 border border-pink-100'
                        }`}
                        onMouseEnter={() => setHoveredSegment('perempuan')}
                        onMouseLeave={() => setHoveredSegment(null)}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="w-6 h-6 bg-pink-500 rounded-full"></div>
                                <div>
                                    <h4 className="text-lg font-bold text-pink-900">Perempuan</h4>
                                    <p className="text-sm text-pink-700">Populasi wanita</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-2xl font-bold text-pink-900">{perempuan.toLocaleString('id-ID')}</p>
                                <p className="text-sm text-pink-700">{percentagePerempuan.toFixed(1)}% dari total</p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Ratio Analysis */}
                    <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Analisis Rasio</h5>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center">
                                <div className="text-lg font-bold text-gray-900">
                                    {perempuan > 0 ? (lakiLaki / perempuan * 100).toFixed(0) : 0}
                                </div>
                                <div className="text-xs text-gray-600">Laki-laki per 100 perempuan</div>
                            </div>
                            <div className="text-center">
                                <div className="text-lg font-bold text-gray-900">
                                    {Math.abs(percentageLakiLaki - percentagePerempuan).toFixed(1)}%
                                </div>
                                <div className="text-xs text-gray-600">Selisih proporsi</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function EnhancedAgeChart({ ageData }: { ageData: RangeUmur[] }) {
    const [showDetails, setShowDetails] = useState(false)
    
    // Format data untuk recharts
    const chartData = ageData.map(item => ({
        range: item.range,
        lakiLaki: item.lakiLaki,
        perempuan: item.perempuan,
        jumlah: item.jumlah,
    }))
    
    // Chart configuration sesuai pattern shadcn
    const chartConfig: ChartConfig = {
        lakiLaki: {
            label: "Laki-laki",
            color: "#3b82f6", // blue-500
        },
        perempuan: {
            label: "Perempuan", 
            color: "#ec4899", // pink-500
        },
    }
    
    return (
        <div className="bg-white p-8 rounded-xl shadow-lg border">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <BarChart3 className="h-6 w-6 text-green-600 mr-2" />
                    Distribusi Usia Komprehensif
                </h3>
            </div>

            {/* Bar Chart sederhana menggunakan Recharts */}
            <div className="bg-white p-6 rounded-xl border shadow-sm mb-6">
                <div className="mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">Bar Chart</h4>
                    <p className="text-sm text-gray-600">Distribusi Penduduk berdasarkan Kelompok Usia dan Jenis Kelamin</p>
                </div>
                <div className="w-full h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis
                                dataKey="range"
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                interval={0}
                                fontSize={12}
                            />
                            <YAxis fontSize={12} />
                            <Tooltip 
                                contentStyle={{
                                    backgroundColor: 'white',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                }}
                                formatter={(value: number, name: string) => [
                                    `${value?.toLocaleString('id-ID')} orang`,
                                    name
                                ]}
                                labelFormatter={(label) => `Kelompok Usia: ${label} tahun`}
                            />
                            <Bar 
                                dataKey="lakiLaki" 
                                fill="#3b82f6"
                                name="Laki-laki"
                                radius={[4, 4, 0, 0]}
                            />
                            <Bar 
                                dataKey="perempuan" 
                                fill="#ec4899"
                                name="Perempuan"
                                radius={[4, 4, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-medium">
                            <BarChart3 className="h-4 w-4" />
                            Bar Chart Distribusi Usia
                        </div>
                        <button
                            onClick={() => setShowDetails(!showDetails)}
                            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center space-x-2"
                        >
                            <span>{showDetails ? 'Sembunyikan Detail' : 'Lihat Detail'}</span>
                            <svg 
                                className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Summary Statistics - Hidden by default */}
            {showDetails && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-2xl font-bold text-blue-900">
                            {ageData.reduce((sum, item) => sum + item.lakiLaki, 0).toLocaleString('id-ID')}
                        </div>
                        <div className="text-sm text-blue-700">Total Laki-laki</div>
                    </div>
                    <div className="p-4 bg-pink-50 rounded-lg border border-pink-200">
                        <div className="text-2xl font-bold text-pink-900">
                            {ageData.reduce((sum, item) => sum + item.perempuan, 0).toLocaleString('id-ID')}
                        </div>
                        <div className="text-sm text-pink-700">Total Perempuan</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="text-2xl font-bold text-green-900">
                            {ageData.reduce((sum, item) => sum + item.jumlah, 0).toLocaleString('id-ID')}
                        </div>
                        <div className="text-sm text-green-700">Total Penduduk</div>
                    </div>
                </div>
            )}

            {/* Detailed Stats Table - Hidden by default */}
            {showDetails && (
                <div className="mt-8">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Detail Statistik per Kelompok Usia</h4>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-200 rounded-lg">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="border border-gray-200 p-3 text-left font-semibold">Kelompok Usia</th>
                                    <th className="border border-gray-200 p-3 text-center font-semibold">Total</th>
                                    <th className="border border-gray-200 p-3 text-center font-semibold">Laki-laki</th>
                                    <th className="border border-gray-200 p-3 text-center font-semibold">Perempuan</th>
                                    <th className="border border-gray-200 p-3 text-center font-semibold">Persentase</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ageData.map((item, index) => (
                                    <tr key={index} className="hover:bg-gray-25">
                                        <td className="border border-gray-200 p-3 font-medium">{item.range} tahun</td>
                                        <td className="border border-gray-200 p-3 text-center font-bold">{item.jumlah.toLocaleString('id-ID')}</td>
                                        <td className="border border-gray-200 p-3 text-center text-blue-700">{item.lakiLaki.toLocaleString('id-ID')}</td>
                                        <td className="border border-gray-200 p-3 text-center text-pink-700">{item.perempuan.toLocaleString('id-ID')}</td>
                                        <td className="border border-gray-200 p-3 text-center font-medium">{item.persentase.toFixed(1)}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

const REGIONAL_DONUT_THRESHOLD = 5
const REGIONAL_DONUT_COLORS = [
    { stroke: '#3b82f6', hsl: 'hsl(217,91%,60%)' },
    { stroke: '#22c55e', hsl: 'hsl(142,71%,45%)' },
    { stroke: '#a855f7', hsl: 'hsl(271,91%,65%)' },
    { stroke: '#f97316', hsl: 'hsl(25,95%,53%)' },
    { stroke: '#ec4899', hsl: 'hsl(330,81%,60%)' },
]

function EnhancedRegionalChart({ dusunData }: { dusunData: PerDusun[] }) {
    const [hoveredDusun, setHoveredDusun] = useState<string | null>(null)
    const sorted = [...dusunData].sort((a, b) => b.jumlah - a.jumlah)
    const total = sorted.reduce((sum, item) => sum + item.jumlah, 0)
    const useBarChart = dusunData.length > REGIONAL_DONUT_THRESHOLD

    const barChartData = sorted.map((item) => ({
        name: item.namaDusun,
        namaDesa: item.namaDesa,
        Jumlah: item.jumlah,
        pct: total > 0 ? ((item.jumlah / total) * 100).toFixed(1) : '0',
    }))

    const barHeight = Math.min(Math.max(300, sorted.length * 28), 520)

    return (
        <div className="bg-white p-8 rounded-xl shadow-lg border">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <PieChartIcon className="h-6 w-6 text-purple-600 mr-2" />
                Analisis Regional Mendalam
            </h3>

            {!useBarChart ? (
                /* Donut chart untuk ≤5 dusun */
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    <div className="flex flex-col items-center justify-center">
                        <div className="relative w-64 h-64">
                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="35" fill="none" stroke="#f3f4f6" strokeWidth="8" />
                                {sorted.map((item, index) => {
                                    const circumference = 2 * Math.PI * 35
                                    const arc = (item.jumlah / total) * circumference
                                    const offset = -sorted.slice(0, index).reduce((sum, prev) => sum + ((prev.jumlah / total) * circumference), 0)
                                    const color = REGIONAL_DONUT_COLORS[index % REGIONAL_DONUT_COLORS.length]
                                    return (
                                        <circle
                                            key={index}
                                            cx="50" cy="50" r="35"
                                            fill="none"
                                            stroke={color.stroke}
                                            strokeWidth={hoveredDusun === item.namaDusun ? '10' : '8'}
                                            strokeDasharray={`${arc} ${circumference}`}
                                            strokeDashoffset={offset}
                                            className="transition-all duration-300 cursor-pointer"
                                            onMouseEnter={() => setHoveredDusun(item.namaDusun)}
                                            onMouseLeave={() => setHoveredDusun(null)}
                                        />
                                    )
                                })}
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-gray-900">{total.toLocaleString('id-ID')}</p>
                                    <p className="text-xs text-gray-500">Total Jiwa</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-2">
                        {sorted.map((item, index) => {
                            const pct = total > 0 ? ((item.jumlah / total) * 100).toFixed(1) : '0'
                            const color = REGIONAL_DONUT_COLORS[index % REGIONAL_DONUT_COLORS.length]
                            return (
                                <div
                                    key={index}
                                    className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${hoveredDusun === item.namaDusun ? 'bg-indigo-50 border-indigo-300' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'}`}
                                    onMouseEnter={() => setHoveredDusun(item.namaDusun)}
                                    onMouseLeave={() => setHoveredDusun(null)}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color.stroke }} />
                                            <div>
                                                <p className="text-sm font-semibold text-gray-800">{item.namaDusun}</p>
                                                {item.namaDesa && <p className="text-xs text-gray-400">{item.namaDesa}</p>}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-bold text-indigo-600">{item.jumlah.toLocaleString('id-ID')}</p>
                                            <p className="text-xs text-gray-500">{pct}%</p>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            ) : (
                /* Bar chart untuk >5 dusun — tampilkan semua data */
                <>
                    <ResponsiveContainer width="100%" height={barHeight}>
                        <BarChart data={barChartData} margin={{ top: 16, right: 16, left: 0, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis
                                dataKey="name"
                                tick={{ fontSize: 11, fill: '#6b7280' }}
                                angle={-40}
                                textAnchor="end"
                                interval={0}
                            />
                            <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={(v) => v.toLocaleString('id-ID')} />
                            <Tooltip
                                contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                                formatter={(value: number) => [`${value.toLocaleString('id-ID')} jiwa`, 'Jumlah']}
                                labelFormatter={(label: string) => {
                                    const item = barChartData.find(d => d.name === label)
                                    return item?.namaDesa ? `${label} — ${item.namaDesa}` : label
                                }}
                            />
                            <Bar dataKey="Jumlah" fill="#6366f1" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                    {/* Grid detail semua dusun */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-4">
                        {sorted.map((item, index) => {
                            const pct = total > 0 ? ((item.jumlah / total) * 100).toFixed(1) : '0'
                            return (
                                <div
                                    key={index}
                                    className={`p-2.5 rounded-lg border transition-all duration-200 cursor-pointer text-center ${hoveredDusun === item.namaDusun ? 'bg-indigo-50 border-indigo-300' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'}`}
                                    onMouseEnter={() => setHoveredDusun(item.namaDusun)}
                                    onMouseLeave={() => setHoveredDusun(null)}
                                >
                                    <p className="text-xs font-semibold text-gray-800 truncate">{item.namaDusun}</p>
                                    {item.namaDesa && <p className="text-xs text-gray-400 truncate">{item.namaDesa}</p>}
                                    <p className="text-sm font-bold text-indigo-600 mt-1">{item.jumlah.toLocaleString('id-ID')}</p>
                                    <p className="text-xs text-gray-500">{pct}%</p>
                                </div>
                            )
                        })}
                    </div>
                </>
            )}
        </div>
    )
}

// Pie Chart for Education Statistics
function DetailPieChart({ data, color }: { data: StatistikItemKategori[], color: string }) {
    const [isSmallScreen, setIsSmallScreen] = useState(false)
    
    // Hook untuk mendeteksi ukuran layar - lebih responsive
    useEffect(() => {
        const checkScreenSize = () => {
            // Menggunakan breakpoint md (768px) dan juga memeriksa orientasi
            const width = window.innerWidth
            const isPortrait = window.innerHeight > window.innerWidth
            setIsSmallScreen(width < 768 || (width < 1024 && isPortrait))
        }
        
        checkScreenSize()
        window.addEventListener('resize', checkScreenSize)
        window.addEventListener('orientationchange', checkScreenSize)
        
        return () => {
            window.removeEventListener('resize', checkScreenSize)
            window.removeEventListener('orientationchange', checkScreenSize)
        }
    }, [])

    const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1', '#d084d0', '#ffb347', '#87ceeb']
    
    // Filter out items with 0 value and separate small percentages
    const filteredData = data.filter(item => item.jumlah > 0)
    const mainItems = filteredData.filter(item => item.persentase >= 4)
    const smallItems = filteredData.filter(item => item.persentase < 4)
    
    // Create pie data
    const pieData = mainItems.map((item, index) => ({
        name: item.nama,
        value: item.jumlah,
        percentage: item.persentase,
        fill: COLORS[index % COLORS.length]
    }))
    
    // Add "Others" category if there are small items
    if (smallItems.length > 0) {
        const othersValue = smallItems.reduce((sum, item) => sum + item.jumlah, 0)
        const othersPercentage = smallItems.reduce((sum, item) => sum + item.persentase, 0)
        
        pieData.push({
            name: 'Lainnya',
            value: othersValue,
            percentage: othersPercentage,
            fill: COLORS[pieData.length % COLORS.length]
        })
    }

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload
            return (
                <div className="bg-white p-3 border rounded-lg shadow-lg">
                    <p className="font-medium">{data.name}</p>
                    <p className="text-sm">Jumlah: {data.value.toLocaleString('id-ID')} orang</p>
                    <p className="text-sm">Persentase: {data.percentage.toFixed(1)}%</p>
                    {data.name === 'Lainnya' && smallItems.length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                            <p className="text-xs font-medium text-gray-600">Termasuk:</p>
                            {smallItems.map((item, index) => (
                                <p key={index} className="text-xs text-gray-500">
                                    {item.nama} ({item.jumlah.toLocaleString('id-ID')} orang, {item.persentase.toFixed(1)}%)
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            )
        }
        return null
    }

    // Responsive layout
    if (isSmallScreen) {
        return (
            <div className="w-full">
                {/* Pie chart tanpa label dan legend untuk mobile */}
                <div className="w-full h-[300px] mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                                label={false} // Disable labels pada mobile
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            {/* Legend dihilangkan pada mobile */}
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                
                {/* Scrollable list untuk mobile - mengikuti tinggi card luar */}
                <div className="max-h-[400px] overflow-y-auto border rounded-lg bg-gray-50">
                    <div className="space-y-2 p-3">
                        <h4 className="text-sm font-semibold text-gray-800 mb-3 sticky top-0 bg-gray-50 py-2">
                            Detail Distribusi Data
                        </h4>
                        {pieData.map((entry, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex items-center space-x-3">
                                    <div 
                                        className="w-4 h-4 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: entry.fill }}
                                    ></div>
                                    <span className="font-medium text-sm text-gray-800">{entry.name}</span>
                                </div>
                                <div className="text-right">
                                    <div className="font-bold text-sm text-gray-900">{entry.value.toLocaleString('id-ID')}</div>
                                    <div className="text-xs text-gray-600">{entry.percentage.toFixed(1)}%</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    // Desktop layout (original)
    return (
        <div className="w-full h-[500px]">
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percentage }) => `${name} (${percentage.toFixed(1)}%)`}
                        outerRadius={150}
                        fill="#8884d8"
                        dataKey="value"
                    >
                        {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                        verticalAlign="bottom" 
                        height={36}
                        formatter={(value, entry) => (
                            <span style={{ color: entry.color }}>
                                {value} ({pieData.find(item => item.name === value)?.value.toLocaleString('id-ID')} orang)
                            </span>
                        )}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    )
}
